import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { BasicButton, PrimaryButton } from 'src/components/widgets/buttons';
import { Dialog } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import Stack from 'src/components/widgets/stack/stack';
import { SupportTicketDropdownTrigger } from 'src/components/widgets/supportTicketDropdown';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { stringFormat, T } from 'src/shared/helpers';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import CheckCircleOutlineIcon from 'src/shared/material_icons/action/check_circle_outline.svg';
import ScheduleIcon from 'src/shared/material_icons/action/schedule.svg';
import ErrorOutlineIcon from 'src/shared/material_icons/alert/error_outline.svg';
import WarningAmberIcon from 'src/shared/material_icons/alert/warning_amber.svg';
import MailOutlineIcon from 'src/shared/material_icons/communication/mail_outline.svg';
import ContentCopyIcon from 'src/shared/material_icons/content/content_copy.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import StorefrontIcon from 'src/shared/material_icons/places/storefront.svg';
import styled from 'styled-components';
import { SwitchDialogWindow } from './switchDialogWindow';
import {
    Body,
    ChannelAvatar,
    ChannelLink,
    ChannelLinkIcon,
    ChannelName,
    ChannelNode,
    ChannelRole,
    DialogTone,
    Header,
    HeaderCaption,
    HeaderIcon,
    HeaderTitle,
    Item,
    ITEM_ICON_COLORS,
    ItemIcon,
    ItemText,
    Layout,
    Overview,
    OverviewChannels,
    SectionTitle,
    TONES
} from './switchDialogParts';

/** A failure is never shown with the success tone. */
type FailureTone = Exclude<DialogTone, 'success'>;

const SUPPORT_EMAIL = 'help@zakeke.com';
const COPIED_FEEDBACK_MS = 2000;

const OverviewNote = styled.p`
    margin: 16px 0 0;
    padding-top: 12px;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
    font-size: 13px;
    text-align: center;
    color: ${(props) => props.theme.app.textColor};
`;

const ErrorDetailsSection = styled(Stack)`
    margin-top: 8px;
`;

const ErrorDetailsBox = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 12px 12px 14px;
    background-color: #f3f5f7;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
`;

const ErrorDetailsText = styled.pre`
    flex: 1;
    margin: 0;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    line-height: 18px;
    white-space: pre-wrap;
    word-break: break-word;
    color: ${(props) => props.theme.app.textColor};
`;

const Footer = styled(Stack)`
    justify-content: flex-end;
    padding-top: 16px;

    ${BasicButton}, ${PrimaryButton} {
        min-width: 110px;
    }
`;

interface FailureStep {
    icon: ReactNode;
    iconVariant: keyof typeof ITEM_ICON_COLORS;
    title: string;
    description: ReactNode;
}

interface SwitchFailureDialogProps {
    tone: FailureTone;
    title: string;
    intro: string;
    masterSalesChannelName: string;
    secondarySalesChannelName: string;
    /** Under the new master's name: why it is not the master. */
    secondaryStatus: string;
    /** Colours the status with the dialog tone (e.g. "Switch not completed"); plain grey otherwise. */
    highlightSecondaryStatus?: boolean;
    overviewNote: string;
    steps: FailureStep[];
    /** The text shown in the Error details box and copied to the clipboard. No text, no Error details section. */
    errorText?: string;
    onClose: () => void;
}

/** Shared layout of the switch failures: what did not change, what the seller can do, and the error text to hand to support. */
const SwitchFailureDialog: FC<SwitchFailureDialogProps> = ({
    tone,
    title,
    intro,
    masterSalesChannelName,
    secondarySalesChannelName,
    secondaryStatus,
    highlightSecondaryStatus,
    overviewNote,
    steps,
    errorText,
    onClose
}) => {
    const [isCopied, setIsCopied] = useState(false);
    const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
        },
        []
    );

    const copyErrorText = () => {
        if (!errorText) return;
        void navigator.clipboard
            .writeText(errorText)
            .then(() => {
                setIsCopied(true);
                if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
                feedbackTimer.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
            })
            .catch(() => setIsCopied(false));
    };

    return (
        <Dialog windowDecorator={SwitchDialogWindow} onClose={onClose}>
            <Layout>
                <Header>
                    <HeaderIcon $tone={tone}>{tone === 'error' ? <ErrorOutlineIcon /> : <WarningAmberIcon />}</HeaderIcon>
                    <div>
                        <HeaderTitle>{title}</HeaderTitle>
                        <HeaderCaption>{intro}</HeaderCaption>
                    </div>
                </Header>

                <Body $gap='24px'>
                    <Overview>
                        <OverviewChannels>
                            <ChannelNode>
                                <ChannelAvatar>
                                    <Icon>
                                        <StorefrontIcon />
                                    </Icon>
                                </ChannelAvatar>
                                <ChannelName title={masterSalesChannelName}>{masterSalesChannelName}</ChannelName>
                                <ChannelRole>{T._('Current master', 'Admin')}</ChannelRole>
                            </ChannelNode>
                            <ChannelLink>
                                <ChannelLinkIcon $color={TONES[tone].color}>{tone === 'error' ? <CloseIcon /> : <ScheduleIcon />}</ChannelLinkIcon>
                            </ChannelLink>
                            <ChannelNode>
                                <ChannelAvatar $dashed>
                                    <Icon>
                                        <StorefrontIcon />
                                    </Icon>
                                </ChannelAvatar>
                                <ChannelName title={secondarySalesChannelName}>{secondarySalesChannelName}</ChannelName>
                                <ChannelRole $tone={highlightSecondaryStatus ? tone : undefined}>{secondaryStatus}</ChannelRole>
                            </ChannelNode>
                        </OverviewChannels>
                        <OverviewNote>{overviewNote}</OverviewNote>
                    </Overview>

                    <Stack $gap='12px'>
                        <SectionTitle>{T._('What to do now', 'Admin')}</SectionTitle>
                        {steps.map((step) => (
                            <Item key={step.title}>
                                <ItemIcon $variant={step.iconVariant}>{step.icon}</ItemIcon>
                                <ItemText>
                                    {step.title}
                                    <span>{step.description}</span>
                                </ItemText>
                            </Item>
                        ))}
                    </Stack>

                    {errorText && (
                        <ErrorDetailsSection $gap='12px'>
                            <SectionTitle>{T._('Error details', 'Admin')}</SectionTitle>
                            <ErrorDetailsBox>
                                <ErrorDetailsText>{errorText}</ErrorDetailsText>
                                <ZTooltip position='top' title={isCopied ? T._('Copied', 'Admin') : T._('Copy', 'Admin')}>
                                    <BasicButton $size='small' $type='icon' aria-label={T._('Copy', 'Admin')} onClick={copyErrorText}>
                                        <Icon>
                                            <ContentCopyIcon />
                                        </Icon>
                                    </BasicButton>
                                </ZTooltip>
                            </ErrorDetailsBox>
                        </ErrorDetailsSection>
                    )}
                </Body>

                <Footer $horizontal $gap='12px'>
                    <BasicButton onClick={onClose}>{T._('Close', 'Admin')}</BasicButton>
                    {/* The same support ticket form as the dashboard's "Contact support", prefilled with what went wrong. */}
                    <SupportTicketDropdownTrigger initialMessage={`${title}\n\n${errorText ?? intro}`}>
                        {({ onClick, isOpen }) => (
                            <PrimaryButton aria-expanded={isOpen} onClick={onClick}>
                                {T._('Contact support', 'Admin')}
                            </PrimaryButton>
                        )}
                    </SupportTicketDropdownTrigger>
                </Footer>
            </Layout>
        </Dialog>
    );
};

const SupportEmailLink: FC = () => <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

/** The switch started, so support has to check it and products must stay untouched meanwhile. */
const inFlightSteps = (): FailureStep[] => [
    {
        icon: <MailOutlineIcon />,
        iconVariant: 'default',
        title: T._('Contact support', 'Admin'),
        description: (
            <>
                {T._('Please contact the Zakeke support team for assistance:', 'Admin')} <SupportEmailLink />
            </>
        )
    },
    {
        icon: <EditIcon />,
        iconVariant: 'warning',
        title: T._('Avoid working on products', 'Admin'),
        description: T._("Don't import, create or edit products until support confirms everything is in order.", 'Admin')
    }
];

interface FailureDialogProps {
    masterSalesChannelName: string;
    secondarySalesChannelName: string;
    errorDetail?: string | null;
    onClose: () => void;
}

/** The custom-plan task failed, so the switch never started. The error box carries the full message, so support gets the context too. */
export const PlanPreparationFailedDialog: FC<FailureDialogProps> = ({ masterSalesChannelName, secondarySalesChannelName, errorDetail, ...actions }) => {
    const intro = T._('We could not prepare the subscription plan required for this sales channel switch.', 'Admin');

    return (
        <SwitchFailureDialog
            tone='error'
            title={T._('Subscription plan preparation failed', 'Admin')}
            intro={intro}
            masterSalesChannelName={masterSalesChannelName}
            secondarySalesChannelName={secondarySalesChannelName}
            secondaryStatus={T._('Not changed', 'Admin')}
            overviewNote={stringFormat(T._("The switch hasn't started. {0} is still your Master Integration.", 'Admin'), masterSalesChannelName)}
            steps={[
                {
                    icon: <MailOutlineIcon />,
                    iconVariant: 'default',
                    title: T._('Contact support', 'Admin'),
                    description: (
                        <>
                            {T._('Write to', 'Admin')} <SupportEmailLink /> {T._('and include the error details below.', 'Admin')}
                        </>
                    )
                },
                {
                    icon: <CheckCircleOutlineIcon />,
                    iconVariant: 'success',
                    title: T._('You can keep working as usual', 'Admin'),
                    description: T._('Nothing has changed on your stores or your subscription.', 'Admin')
                }
            ]}
            errorText={errorDetail ? `${intro}\n\n${T._('Error details:', 'Admin')} ${errorDetail}` : intro}
            {...actions}
        />
    );
};

/** A task of the switch failed after it started, so part of the products may already be on the new master. */
export const SwitchFailedDialog: FC<FailureDialogProps> = ({ masterSalesChannelName, secondarySalesChannelName, errorDetail, ...actions }) => (
    <SwitchFailureDialog
        tone='error'
        title={T._('Sales Channel Switch Failed', 'Admin')}
        intro={T._('An error occurred while processing the sales channel switch.', 'Admin')}
        masterSalesChannelName={masterSalesChannelName}
        secondarySalesChannelName={secondarySalesChannelName}
        secondaryStatus={T._('Switch not completed', 'Admin')}
        highlightSecondaryStatus
        overviewNote={T._('The switch started but stopped before finishing. Some products may not have been switched yet.', 'Admin')}
        steps={inFlightSteps()}
        errorText={errorDetail || T._('No error details available.', 'Admin')}
        {...actions}
    />
);

/** The switch has been polling for more than 2 hours: not a failure yet, but support has to look at it. */
export const SwitchTakingTooLongDialog: FC<Omit<FailureDialogProps, 'errorDetail'>> = ({ masterSalesChannelName, secondarySalesChannelName, ...actions }) => (
    <SwitchFailureDialog
        tone='warning'
        title={T._('Sales Channel Switch Taking Too Long', 'Admin')}
        intro={T._("The sales channel switch is taking longer than expected and hasn't completed yet.", 'Admin')}
        masterSalesChannelName={masterSalesChannelName}
        secondarySalesChannelName={secondarySalesChannelName}
        secondaryStatus={T._('Switch not completed', 'Admin')}
        highlightSecondaryStatus
        overviewNote={T._("The switch started but hasn't finished yet. Our team needs to check it.", 'Admin')}
        steps={inFlightSteps()}
        {...actions}
    />
);
